import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Mail,
  MessageSquare,
  Settings,
  X,
  Shield,
  Mic2,
  Building2,
  ShieldBan,
  AlertTriangle,
  BarChart3,
  HeadphonesIcon,
  ClipboardCheck,
  DollarSign,
  Newspaper,
  Briefcase,
  FileText,
  Bug,
  Palette,
  Trash2,
  UserCog,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAdminSidebarCounts } from "@/hooks/useAdminSidebarCounts";
import { useAuth } from "@/contexts/AuthContext";

interface AdminSidebarProps {
  onNavigate?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  exact?: boolean;
  hasBadge?: boolean;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    // Dashboard - no label (standalone)
    items: [
      { to: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    ]
  },
  {
    label: "Platform",
    items: [
      { to: "/admin/users", icon: Users, label: "User Management" },
      { to: "/admin/network-admins", icon: UserCog, label: "Network Admins" },
      { to: "/admin/podcasts", icon: Mic2, label: "Podcasts" },
      { to: "/admin/trashed-episodes", icon: Trash2, label: "Trashed Episodes" },
      { to: "/admin/approval-queue", icon: ClipboardCheck, label: "Approval Queue", hasBadge: true },
      { to: "/admin/revenue-entries", icon: DollarSign, label: "Revenue Entries" },
      { to: "/admin/withdrawals", icon: DollarSign, label: "Withdrawals" },
      { to: "/admin/expenses", icon: DollarSign, label: "Expenses" },
      { to: "/admin/sync-exclusions", icon: ShieldBan, label: "Sync Exclusions" },
      { to: "/admin/failed-syncs", icon: AlertTriangle, label: "Failed Syncs", hasBadge: true },
      { to: "/admin/support", icon: HeadphonesIcon, label: "Support", hasBadge: true },
    ]
  },
  {
    label: "Insights",
    items: [
      { to: "/admin/analytics", icon: BarChart3, label: "Platform Analytics" },
    ]
  },
  {
    label: "Studios",
    items: [
      { to: "/admin/studios", icon: Building2, label: "Studios" },
      { to: "/admin/studio-bookings", icon: ClipboardCheck, label: "Studio Bookings", hasBadge: true },
    ]
  },
  {
    label: "Communications",
    items: [
      { to: "/admin/articles", icon: Newspaper, label: "Articles" },
      { to: "/admin/email-templates", icon: Mail, label: "Email Templates" },
      { to: "/admin/sms-templates", icon: MessageSquare, label: "SMS Templates" },
    ]
  },
  {
    label: "Company",
    items: [
      { to: "/admin/careers", icon: Briefcase, label: "Career Postings" },
      { to: "/admin/job-applications", icon: FileText, label: "Applications", hasBadge: true },
    ]
  },
  {
    label: "System",
    items: [
      { to: "/admin/bugs", icon: Bug, label: "Bug Reports", hasBadge: true },
      { to: "/admin/theme", icon: Palette, label: "Theme Options" },
      { to: "/admin/settings", icon: Settings, label: "Settings" },
    ]
  },
];

function GroupLabel({ label }: { label: string }) {
  return (
    <div className="px-3 py-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-sidebar-muted/70">
        {label}
      </span>
    </div>
  );
}

// Routes a network admin can see. Anything else is platform-admin-only and gets filtered out.
const NETWORK_ADMIN_ALLOWED = new Set<string>([
  "/admin/users",
  "/admin/podcasts",
  "/admin/approval-queue",
  "/admin/analytics",
]);

export function AdminSidebar({ onNavigate, showCloseButton, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const counts = useAdminSidebarCounts();
  const { hasRole } = useAuth();
  const isPlatformAdmin = hasRole("admin");

  const visibleGroups = isPlatformAdmin
    ? navGroups
    : navGroups
        .map((g) => ({ ...g, items: g.items.filter((i) => NETWORK_ADMIN_ALLOWED.has(i.to)) }))
        .filter((g) => g.items.length > 0);

  const isActive = (to: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === to;
    }
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      {/* Close Button for drawer mode */}
      {showCloseButton && onClose && (
        <div className="flex h-14 items-center px-4 border-b border-sidebar-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>
      )}

      {/* Admin Badge */}
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10">
          <Shield className="h-4 w-4 text-accent" />
          <span className="text-sm font-medium text-accent">
            {isPlatformAdmin ? "Admin Panel" : "Network Admin"}
          </span>
        </div>
      </div>

      {/* Back to Podcaster Portal */}
      <div className="p-4 border-b border-sidebar-border">
        <NavLink
          to="/dashboard"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors bg-sidebar-accent/30 text-sidebar-foreground hover:bg-sidebar-accent/50"
        >
          <Mic2 className="h-4 w-4" />
          Podcaster Portal
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        {visibleGroups.map((group, groupIndex) => (
          <div 
            key={groupIndex} 
            className={cn(
              groupIndex > 0 && "border-t border-sidebar-border pt-3 mt-3"
            )}
          >
            {group.label && <GroupLabel label={group.label} />}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive(item.to, item.exact)
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span className="flex-1">{item.label}</span>
                  {item.hasBadge && (counts[item.to] ?? 0) > 0 && (
                    <Badge variant="destructive" className="h-5 min-w-[20px] px-1.5 text-xs">
                      {counts[item.to]}
                    </Badge>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Version info */}
      <div className="p-4 border-t border-sidebar-border">
        <p className="text-xs text-sidebar-muted px-3">
          Admin Panel v1.0
        </p>
      </div>
    </div>
  );
}
